// Configuración de TanStack Query: reintentos solo en errores transitorios, datos frescos 30 s,
// avisos de error centralizados (UX §2.8, §15.3) y refresco al volver a la pestaña.
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/Toaster';
import { isApiError, type ApiError } from './api';
import { qk } from './queryKeys';
import { t } from './i18n/es-AR';

export interface AtteQueryMeta extends Record<string, unknown> {
  /** Mostrar un toast si la consulta falla (por defecto no: la pantalla muestra su estado de error). */
  toastError?: boolean;
}

export interface AtteMutationMeta extends Record<string, unknown> {
  /** `false` para manejar el error en la pantalla (formularios con error en línea). Por defecto toast. */
  toastError?: boolean;
  /** Texto del toast de éxito ("Turno cancelado"). */
  successToast?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: Error;
    queryMeta: AtteQueryMeta;
    mutationMeta: AtteMutationMeta;
  }
}

/** Errores que se reintentan solos: red, tiempo agotado y 5xx (nunca 4xx). */
export function shouldRetry(failureCount: number, error: unknown, max = 2): boolean {
  if (failureCount >= max) return false;
  if (isApiError(error)) return error.isTransient;
  return false;
}

/** Errores que ya muestra la pantalla en línea (no van a un toast genérico). */
function isInlineError(e: ApiError): boolean {
  if (e.code === 'validation_error') return true;
  if (e.code === 'unauthenticated') return true;
  if (e.code === 'conflict' && (e.reason === 'needs_confirmation' || e.reason === 'stale_version')) return true;
  return false;
}

export function createQueryClient(): QueryClient {
  // Referencia tardía: los ganchos necesitan el cliente ya creado.
  // eslint-disable-next-line prefer-const
  let client: QueryClient;

  const onForbidden = (e: ApiError) => {
    if (e.code === 'module_disabled') {
      toast.warning(t.errors.moduleOffLive, { id: 'module-disabled' });
      void client.invalidateQueries({ queryKey: qk.session });
      return true;
    }
    if (e.code === 'forbidden' && e.reason !== 'csrf' && e.reason !== 'read_only_view') {
      toast.error(t.errors.forbiddenAction, { id: 'forbidden' });
      void client.invalidateQueries({ queryKey: qk.session });
      return true;
    }
    return false;
  };

  client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isApiError(error)) return;
        // 403 en lecturas de un módulo apagado: aviso y se recarga la navegación (§15.4).
        if (error.code === 'module_disabled') {
          onForbidden(error);
          return;
        }
        if (query.meta?.toastError && !isInlineError(error)) toast.error(error.message);
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _vars, _ctx, mutation) => {
        const msg = mutation.meta?.successToast;
        if (msg) toast.success(msg);
      },
      onError: (error, _vars, _ctx, mutation) => {
        if (mutation.meta?.toastError === false) return;
        if (!isApiError(error)) {
          toast.error(t.errors.internal);
          return;
        }
        if (error.status === 403 && onForbidden(error)) return;
        if (isInlineError(error)) return;
        toast.error(error.message);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (n, e) => shouldRetry(n, e),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: { retry: false },
    },
  });
  return client;
}
